<template>
  <v-tab-item>
    <v-layout justify-space-between>
      <v-flex md2>
        <v-btn dark color="primary" @click.stop="showCommodityTemplateDialog()"><v-icon dark>add</v-icon>{{ $t('commodityTemplate') }}</v-btn>
      </v-flex>
      <v-flex md2>
        <v-text-field
          append-icon="filter_list"
          class="text-xs-right"
          :label="$t('search')"
          single-line
          hide-details
          v-model="filter"
          clearable
        ></v-text-field>
      </v-flex>
    </v-layout>
    <v-data-table
      :headers="commodityTemplateHeaders"
      :items="commodityTemplates"
      item-key="key"
      class="my-1 elevation-2 myDense"
      :rows-per-page-items="rowPerPage"
      :search="filter"
    >
      <template v-slot:items="props">
        <td class="text-xs-left">
          {{ props.item.name }}
        </td>
        <td class="text-xs-left">
          {{ props.item.description || '-' }}
        </td>
        <td class="text-xs-left">
          <v-chip v-for="(item, index) in props.item.commodities.slice(0, 3)" :key="index" small>
            {{ item.customDescription || item.scheduleBDescription || $t('commodity') }}
          </v-chip>
          <span v-if="props.item.commodities.length > 3" class="grey--text">
            +{{ props.item.commodities.length - 3 }} {{ $t('more') }}
          </span>
        </td>
        <td class="text-xs-center">
          <v-layout row>
            <v-flex>
              <v-btn
                dark
                flat
                color="primary"
                @click.stop="showCommodityTemplateDialog(props.item)"
              >{{ $t('edit') }}</v-btn>
            </v-flex>
            <v-flex>
              <v-btn
                dark
                flat
                color="primary"
                @click.stop="deleteCommodityTemplate(props.item)"
              >{{ $t('delete') }}</v-btn>
            </v-flex>
          </v-layout>
        </td>
      </template>
    </v-data-table>

    <FormSubmitPopup
      :title="`${isEdit ? $t('edit') : $t('add')} ${$t('commodityTemplate')}`"
      v-model="createCommodityTemplateDialog"
      v-if="createCommodityTemplateDialog"
      large
      @popupClose="closeDialog"
      :rightMethod="submit"
      :rightButtonText="$t('submit')"
    >
      <template v-slot:input>
        <v-form ref="form" v-model="validForm">
          <v-layout row wrap>
            <v-flex xs12 md3>
              <v-text-field
                v-model.trim="template.name"
                :label="$t('name')"
                :rules="[fieldIsRequired($t('name')), isTemplateNameUnique]"
                class="required_field"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md8>
              <v-text-field
                v-model.trim="template.description"
                :label="$t('description')"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-divider class="my-3"></v-divider>
          <v-subheader>{{ $t('commodities') }}</v-subheader>

          <v-layout class="red--text font-weight-bold text-xs-right" v-if="commodityErrorMessage">
            {{ commodityErrorMessage }}
          </v-layout>

          <v-data-table
            :headers="commodityHeaders"
            :items="template.commodities"
            hide-actions
            class="elevation-2 mt-2"
          >
            <template v-slot:headerCell="props">
              <span v-if="props.header.value === 'action'">
                <v-tooltip top>
                  <template v-slot:activator="tooltip">
                    <div v-on="tooltip.on">
                      <v-btn color="primary" flat small icon @click="addEmptyCommodity"><v-icon>add</v-icon></v-btn>
                    </div>
                  </template>
                  {{ $t('add') }}
                </v-tooltip>
              </span>
              <span v-else>{{ props.header.text }}</span>
            </template>
            <template v-slot:items="props">
              <tr class="commodity-row">
                <td class="text-xs-center">
                  <v-tooltip top>
                    <template v-slot:activator="{on}">
                      <v-btn color="error" flat small icon v-on="on" @click="deleteCommodity(props.index)" :disabled="template.commodities.length === 1"><v-icon>delete</v-icon></v-btn>
                    </template>
                    <span>{{ $t('delete') }}</span>
                  </v-tooltip>
                </td>
                <td class="text-xs-left">
                  <v-layout row>
                    <v-autocomplete
                      class="table-item"
                      style="max-width: 200px;"
                      :value="props.item.selectedCommodity"
                      :items="mergedCommoditiesList"
                      item-text="scheduleBDescription"
                      item-value="scheduleBDescription"
                      :filter="customCommodityFilter"
                      clearable
                      :label="$t('commodity')"
                      :placeholder="$t('commodities')"
                      @change="onComboboxChangeByCommodities($event, props.index)"
                      @input="onComboboxChangeByCommodities($event, props.index)"
                      :rules="[fieldIsRequired($t('commodity'))]"
                      return-object
                    >
                      <template v-slot:item="data">
                        <v-list-tile-content style="max-width: 300px; white-space: normal;">
                          <v-list-tile-title style="white-space: normal; word-wrap: break-word;">{{ data.item.scheduleBDescription || data.item.description }}</v-list-tile-title>
                          <v-list-tile-sub-title style="white-space: normal; word-wrap: break-word;">
                            Code: {{ data.item.scheduleBCode }}
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                      <template v-slot:no-data>
                        <v-list-tile>
                          <v-list-tile-content>
                            <v-list-tile-title>
                              {{ $t('noDataAvailable') }}
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </template>
                    </v-autocomplete>
                    <v-text-field
                      class="table-item"
                      style="max-width: 250px;"
                      v-model.trim="props.item.scheduleBDescription"
                      :label="$t('description')"
                      :placeholder="$t('description')"
                      :rules="[fieldIsRequired($t('description')), fieldCharLimit('description', 35)]"
                    ></v-text-field>
                  </v-layout>
                </td>
                <td class="text-xs-left">
                  <v-layout row>
                    <v-autocomplete
                      class="table-item"
                      v-model="props.item.countryOfOrigin"
                      :items="countryCodeArray"
                      :label="$t('countryOfOrigin')"
                      item-text="name"
                      item-value="value"
                      :rules="[fieldIsRequired($t('countryOfOrigin'))]"
                    ></v-autocomplete>
                    <v-text-field
                      class="table-item"
                      v-model.trim="props.item.harmonizedCode"
                      @input="onHarmonizedCodeInput(props.item)"
                      :rules="[validateHsCode]"
                      :class="{'required_field': isHsCodeRequired(props.item)}"
                    ></v-text-field>
                  </v-layout>
                </td>
                <td class="text-xs-left">
                  <v-text-field
                    :rules="[
                      fieldIsRequired(),
                      fieldIsInteger(),
                      fieldIsOverZero()
                    ]"
                    class="required_field table-item qty_unit_filed"
                    v-model.number="props.item.quantity"
                    :label="$t('quantity')"
                    @input="onQuantityInput(props.item)"
                  >
                    <template slot="append-outer">
                      <v-autocomplete
                        class="qty_unit required_field"
                        :items="inShowChannelUnionList"
                        v-model="props.item.selectedUnit"
                        :rules="[fieldIsRequired($t('unit'))]"
                        item-text="text"
                        item-value="key"
                        :label="$t('unit')"
                      ></v-autocomplete>
                    </template>
                  </v-text-field>
                </td>
                <td class="text-xs-left" v-if="hasCommodityQty2">
                  <v-text-field
                    v-if="isShowQty2(props.item)"
                    :rules="[
                      fieldIsRequired(),
                      fieldIsInteger(),
                      fieldIsOverZero()
                    ]"
                    class="required_field table-item qty_unit_filed"
                    v-model.number="props.item.quantity2"
                    :label="$t('quantity')"
                  >
                    <template slot="append-outer">
                      <v-autocomplete
                        class="qty_unit required_field"
                        :items="inShowUnit2List"
                        v-model="props.item.quantity2Units"
                        :rules="[fieldIsRequired($t('unit'))]"
                        item-text="text"
                        item-value="key"
                        :label="$t('unit')"
                      ></v-autocomplete>
                    </template>
                  </v-text-field>
                  <v-text-field
                    v-if="!isShowQty2(props.item)"
                    :rules="[
                      fieldIsRequired(),
                      fieldIsInteger(),
                      fieldIsOverZero()
                    ]"
                    class="required_field pt-0 table-item qty_unit_filed"
                    :label="$t('quantity')"
                    disabled
                  >
                    <template slot="append-outer">
                      <v-autocomplete
                        class="qty_unit required_field"
                        :items="inShowUnit2List"
                        :rules="[fieldIsRequired($t('unit'))]"
                        item-text="text"
                        item-value="key"
                        disabled
                        :label="$t('unit')"
                      ></v-autocomplete>
                    </template>
                  </v-text-field>
                </td>
                <td class="text-xs-left">
                  <v-text-field
                    :label="$t('price')"
                    class="table-item required_field"
                    v-model.number="props.item.price"
                    @input="onPriceInput(props.item)"
                    prefix="$"
                    :rules="[fieldIsOverZero($t('price'))]"
                  ></v-text-field>
                </td>
                <td class="text-xs-left">
                  <v-text-field
                    :label="$t('customsValue')"
                    class="table-item required_field"
                    :value="props.item.customsValue"
                    prefix="$"
                    :disabled="true"
                  ></v-text-field>
                </td>
                <td class="text-xs-left">
                  <v-text-field
                    :label="$t('weight')"
                    class="table-item required_field"
                    v-model.number="props.item.commodityWeight"
                    :suffix="isMeasurementMetric ? 'kg' : 'Lbs'"
                    :rules="[fieldIsOverZero($t('weight'))]"
                  ></v-text-field>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-form>
      </template>
    </FormSubmitPopup>
  </v-tab-item>
</template>

<script>
import { mapGetters } from 'vuex'
import { checkRules, commodityTools } from '@/utils/tools'
import FormSubmitPopup from './FormSubmitPopup.vue'

export default {
  name: 'CommodityTemplateAdmin',
  mixins: [checkRules, commodityTools],
  components: {
    FormSubmitPopup
  },
  data () {
    return {
      validForm: false,
      template: {
        name: '',
        description: '',
        commodities: []
      },
      filter: '',
      rowPerPage: [15, 30, 50, {text: this.$t('all'), value: -1}],
      createCommodityTemplateDialog: false,
      commodityTemplateInEdit: {},
      commodityErrorMessage: '',
      commodityTemplateHeaders: [
        { text: this.$t('name'), value: 'name', align: 'left', sortable: false, width: '20%' },
        { text: this.$t('description'), value: 'description', align: 'left', sortable: false, width: '30%' },
        { text: this.$t('commodities'), value: 'commodities', align: 'left', sortable: false, width: '40%' },
        { text: this.$t('actions'), value: '', align: 'center', sortable: false, width: '10%' }
      ],
      upsUnites2Map: {
        'PCS': 'Pieces',
        'KG': 'Kilogram',
        'L': 'Liter',
        'M': 'Meter',
        'M2': 'Square Meters',
        'M3': 'Cubic Meters',
        'NO': 'Number',
        'PRS': 'Pairs',
        'DOZ': 'Dozen',
        'DPC': 'Dozen Pieces',
        'DPR': 'Dozen Pairs',
        'FBM': 'Fiber Meter',
        'GCN': 'Gross Containers',
        'GM': 'Gram',
        'GRS': 'Gross',
        'HUN': 'Hundred',
        'KM3': '1,000 Cubic Meters',
        'KTS': 'Kilogram Total Sugars',
        'MC': 'Millicurie',
        'PK': 'Pack',
        'PFL': 'Proof Liter',
        'RBA': 'Running Bales',
        'SQ': 'Square',
        'T': 'Ton',
        'THS': '1,000',
        'X': 'No Quantity required'
      },
      fedexHsCodeMap: {
        'AR': 'CARAT',
        'CM': 'CENTIMETER',
        'CFT': 'CUBIC FOOT',
        'DOZ': 'DOZEN',
        'DPR': 'DOZEN PAIR',
        'EA': 'EACH',
        'LFT': 'FOOT',
        'G': 'GRAM',
        'GR': 'GROSS',
        'KG': 'KILOGRAM',
        'LTR': 'LITER',
        'LB': 'POUND',
        'LNM': 'LINEAR METER',
        'M': 'METER',
        'M2': 'SQUARE METER (M2)',
        'M3': 'CUBIC METER',
        'MG': 'MILLIGRAM',
        'ML': 'MILLILITER',
        'NO': 'NUMBER',
        'OZ': 'OUNCE',
        'PR': 'PAIR',
        'PCS': 'PIECE',
        'SFT': 'SQUARE FOOT',
        'SYD': 'SQUARE YA,',
        'YD': 'YARD'
      }
    }
  },
  computed: {
    ...mapGetters(['commoditiesList', 'countryCodeArray', 'isMeasurementMetric', 'commodityTemplates']),
    isEdit () {
      return !!this.commodityTemplateInEdit?.key
    },
    mergedCommoditiesList () {
      const customOption = {
        scheduleBDescription: 'Commodity Desc',
        description: 'Commodity Desc',
        scheduleBCode: '',
        harmonizedCode: '',
        origin: 'US',
        countryOfOrigin: 'US',
        unit: '',
        unit2: '',
        unitOfMeasure: '',
        category: '',
        _isCustom: true
      }

      if (this.commoditiesList && this.commoditiesList.length > 0) {
        const mappedList = this.commoditiesList.map(item => {
          const origin = item.origin
          let countryOfOrigin = 'US'
          if (origin === 'D') {
            countryOfOrigin = 'US'
          } else if (origin === 'F') {
            countryOfOrigin = item.countryCode || 'CN'
          }
          return {
            scheduleBDescription: item.itemDesc || item.commodityDetail || '',
            description: item.itemDesc || item.commodityDetail || '',
            scheduleBCode: item.scheduleBCode,
            harmonizedCode: item.scheduleBCode,
            origin: origin,
            countryOfOrigin: countryOfOrigin,
            unit: item.unit,
            unit2: item.unit2,
            unitOfMeasure: item.unit,
            category: item.category
          }
        })
        return [customOption, ...mappedList]
      }
      return this.organizationCommodities || []
    },
    commodityHeaders () {
      const totalCustomsValue = this.template.commodities.reduce((acc, item) => acc + (item.customsValue || 0), 0)
      const totalQty = this.template.commodities.reduce((acc, item) => acc + (item.quantity || 0), 0)
      const totalWeight = this.template.commodities.reduce((acc, item) => acc + (item.commodityWeight || 0), 0)

      const hasQty2 = this.template.commodities.some(item => this.isShowQty2(item))

      const headers = [
        { text: this.$t('actions'), value: 'action', align: 'center', sortable: false, width: '80px' },
        { text: this.$t('commodity'), value: 'scheduleBDescription', align: 'left', sortable: false, width: '280px' },
        { text: `${this.$t('countryOfOrigin')} / ${this.$t('hsCode')}`, value: 'harmonizedCode', align: 'left', sortable: false, width: '200px' },
        { text: `${this.$t('qty')}(${totalQty})`, value: 'quantity', align: 'left', sortable: false, width: '220px' }
      ]

      if (hasQty2) {
        headers.push({ text: this.$t('qty2'), value: 'quantity2', align: 'left', sortable: false, width: '200px' })
      }

      headers.push(
        { text: this.$t('price'), value: 'price', align: 'left', sortable: false, width: '120px' },
        { text: `${this.$t('customsValue')}($${totalCustomsValue.toLocaleString()})`, value: 'customsValue', align: 'left', sortable: false, width: '140px' },
        { text: `${this.$t('totalWeight')}(${this.isMeasurementMetric ? 'kg' : 'Lbs'})(${totalWeight})`, value: 'commodityWeight', align: 'left', sortable: false, width: '160px' }
      )

      return headers
    },
    hasCommodityQty2 () {
      return this.template.commodities.some(item => this.isShowQty2(item))
    },
    commoditiesMap () {
      const map = {}
      if (this.commoditiesList && this.commoditiesList.length > 0) {
        this.commoditiesList.forEach(item => {
          if (item.scheduleBCode) {
            map[item.scheduleBCode] = {
              uom: item.unit,
              uom1: item.unit2 || 'NULL'
            }
          }
        })
      }
      return map
    },
    fedexHsCodeList () {
      return Object.entries(this.fedexHsCodeMap).map(([abbr]) => {
        return {text: abbr, key: abbr}
      })
    },
    upsUnites2List () {
      return Object.entries(this.upsUnites2Map).map(([abbr]) => {
        return {text: `${abbr}`, key: abbr}
      })
    },
    inShowChannelUnionList () {
      return this.fedexHsCodeList
    },
    inShowUnit2List () {
      return this.upsUnites2List
    }
  },
  methods: {
    cloneCommodities (commodities) {
      return commodities.map(item => ({
        ...item,
        selectedCommodity: this.mergedCommoditiesList.find(c => c.scheduleBDescription === item.scheduleBDescription) || this.mergedCommoditiesList.find(c => c._isCustom)
      }))
    },
    closeDialog () {
      this.createCommodityTemplateDialog = false
      this.commodityErrorMessage = ''
      this.template = {
        name: '',
        description: '',
        commodities: []
      }
    },
    showCommodityTemplateDialog (item = {}) {
      this.createCommodityTemplateDialog = true
      this.commodityTemplateInEdit = item
      if (item && item.key) {
        this.template = {
          name: item.name || '',
          description: item.description || '',
          commodities: this.cloneCommodities(item.commodities || [])
        }
      } else {
        this.template = {
          name: '',
          description: '',
          commodities: []
        }
        this.addEmptyCommodity()
      }
    },
    addEmptyCommodity () {
      this.template.commodities.push({
        scheduleBCode: '',
        scheduleBDescription: '',
        harmonizedCode: '',
        countryOfOrigin: 'US',
        quantity: 1,
        selectedUnit: '',
        quantity2: null,
        quantity2Units: '',
        price: 0,
        customsValue: 0,
        commodityWeight: 0,
        selectedCommodity: null
      })
    },
    deleteCommodity (index) {
      if (this.template.commodities.length === 1) return
      this.template.commodities.splice(index, 1)
    },
    getCommodityUpdates (selectedItem, isCommodityUnchangeable = false) {
      if (selectedItem === null || selectedItem === '') {
        return {
          scheduleBCode: '',
          selectedCommodity: null,
          exportInformationCode: 'OS',
          countryOfOrigin: 'US',
          licenseExceptionSymbol: 'C33',
          isCustomCommodity: false,
          needsManualDescription: false,
          scheduleBDescription: ''
        }
      }

      if (typeof selectedItem !== 'object') {
        return {}
      }

      const item = selectedItem

      if (isCommodityUnchangeable) {
        const description = item.scheduleBDescription || item.description || ''
        return {
          scheduleBCode: item.scheduleBCode || '',
          countryOfOrigin: item.countryOfOrigin || 'US',
          scheduleBDescription: description,
          unitOfMeasure: item.unitOfMeasure || item.unit || this.getUnitOfMeasure(item.scheduleBCode, '')
        }
      }

      if (item._isCustom) {
        return {
          scheduleBDescription: '',
          scheduleBCode: '',
          selectedCommodity: item,
          countryOfOrigin: 'US',
          isCustomCommodity: true,
          needsManualDescription: false,
          unitOfMeasure: '',
          selectedUnit: ''
        }
      }

      const {
        scheduleBDescription,
        description,
        scheduleBCode,
        origin,
        countryOfOrigin,
        unit2
      } = item

      const fullDescription = scheduleBDescription || description || ''
      const isDescriptionTooLong = fullDescription.length > 35

      const updates = {
        scheduleBDescription: isDescriptionTooLong ? '' : fullDescription,
        needsManualDescription: isDescriptionTooLong,
        scheduleBCode: scheduleBCode || '',
        selectedCommodity: item,
        isCustomCommodity: false,
        countryOfOrigin: this.getCountryOfOriginFromItem(origin, countryOfOrigin)
      }

      const unitOfMeasure = this.getUnitOfMeasure(scheduleBCode, '')
      updates.unitOfMeasure = unitOfMeasure
      updates.selectedUnit = unitOfMeasure

      if (unit2) {
        updates.quantity2Units = unit2
      }

      return updates
    },
    
    onComboboxChangeByCommodities (commodityItem, index) {
      const commodity = this.template.commodities[index]
      const updates = this.getCommodityUpdates(commodityItem || null, false)
      Object.assign(commodity, updates)
    },
    onHarmonizedCodeInput () {
      // HS Code input handling
    },
    onQuantityInput (commodity) {
      if (!commodity.customsValue && commodity.price) {
        commodity.customsValue = commodity.quantity * commodity.price
      }
    },
    onPriceInput (commodity) {
      if (commodity.quantity && commodity.price) {
        commodity.customsValue = commodity.quantity * commodity.price
      }
    },
    isShowQty2 (item) {
      const uomData = this.commoditiesMap[item.scheduleBCode]
      return uomData && uomData.uom1 && uomData.uom1 !== 'NULL'
    },
    isHsCodeRequired () {
      // Add HS Code requirement logic if needed
      return false
    },
    validateHsCode (value) {
      if (!value) return true // Empty is valid
      if (value.length < 6 || value.length > 15 || isNaN(+value)) {
        return this.$t('hsCodeValidation')
      }
      return true
    },
    isTemplateNameUnique (value) {
      if (!value) return true
      const isDuplicate = this.commodityTemplates.some(template => {
        // Skip the current template being edited
        if (this.commodityTemplateInEdit && this.commodityTemplateInEdit.key === template.key) {
          return false
        }
        return template.name === value
      })
      return !isDuplicate || this.$t('nameCanNotBeDuplicated')
    },
    getCountryOfOriginFromItem (origin, countryOfOrigin) {
      if (origin === 'D') return 'US'
      if (origin === 'F') return countryOfOrigin || 'CN'
      return countryOfOrigin || 'US'
    },
    getUnitOfMeasure (scheduleBCode = '', carrier = '') {
      let unitOfMeasure = ''
      carrier = carrier.trim().toUpperCase()
      const matchedItem = this.commoditiesMap[scheduleBCode]
      if (!matchedItem) return unitOfMeasure

      if (matchedItem.uom === 'NO') {
        unitOfMeasure = (carrier.includes('UPS') && 'PCS') || (carrier.includes('FEDEX') && 'NO') || matchedItem.uom
      } else {
        unitOfMeasure = matchedItem.uom
      }
      return unitOfMeasure
    },
    submit () {
      if (!this.$refs.form.validate()) return Promise.reject(new Error('Validation failed'))

      const payload = {
        name: this.template.name,
        description: this.template.description,
        commodities: this.template.commodities.map(item => ({
          scheduleBCode: item.scheduleBCode,
          scheduleBDescription: item.scheduleBDescription,
          harmonizedCode: item.harmonizedCode,
          countryOfOrigin: item.countryOfOrigin,
          quantity: item.quantity,
          selectedUnit: item.selectedUnit,
          quantity2: item.quantity2,
          quantity2Units: item.quantity2Units,
          price: item.price,
          customsValue: item.customsValue,
          commodityWeight: item.commodityWeight
        }))
      }

      if (this.commodityTemplateInEdit && this.commodityTemplateInEdit.key) {
        payload.key = this.commodityTemplateInEdit.key
      }

      return this.$store.dispatch('updateCommodityTemplate', payload).then(() => {
        this.closeDialog()
      })
    },
    deleteCommodityTemplate (item) {
      if (!confirm(this.$t('confirmDelete', { name: 'commodity template' }))) return Promise.resolve()
      return this.$store.dispatch('deleteCommodityTemplate', item)
    }
  },
  mounted () {
    this.$store.dispatch('getCommodityTemplates')
  }
}
</script>

<style scoped>
.table-item {
  padding-bottom: 4px;
}

.qty_unit_filed /deep/ .v-input__append-outer {
  margin-top: 0px !important;
}
.qty_unit_filed .v-input {
  margin-top: 0px;
}
.qty_unit_filed .qty_unit {
  margin: 0;
  padding: 0;
}

.qty_unit_filed .qty_unit /deep/ label::after {
  content: "";
}
.myDense {
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/CommodityTemplateTab.vue